import { Link } from "react-router-dom";
import DcPage from "@/components/landing/dcv2/DcPage";
import { Lock, Shield, Server, KeyRound, Database, FileCheck } from "lucide-react";
import { AmbientField, Spotlight } from "@/motion";

/**
 * Security & Compliance.
 *
 * The audit and the design critique independently flagged the same gap: HR and
 * accounting data invites diligence questions, and the site answered them with
 * three unlinked badges.
 *
 * The three sections below are deliberately separated, because conflating them
 * is what produced "SOC 2 Type II Ready" presented as a certification badge:
 *
 *   CONTROLS      — what is implemented. Sourced from the platform's own
 *                   previously published specs.
 *   CERTIFICATIONS— what AI2Aim currently holds. NOT asserted here, because no
 *                   certification has been confirmed. Do not add one to this
 *                   page without the actual report in hand.
 *   ROADMAP       — what is being pursued. Left to the business to state.
 *
 * Nothing on this page may claim an audit, an SLA, or an uptime figure that
 * does not have a document behind it.
 */
const CONTROLS = [
  {
    icon: Lock,
    label: "Encryption",
    desc: "AES-256 encryption for data at rest, with encrypted transport for data in transit.",
  },
  {
    icon: KeyRound,
    label: "Authentication",
    desc: "OAuth 2.0 authentication with role-based access control governing what each account can reach.",
  },
  {
    icon: Shield,
    label: "Access Control",
    desc: "Role-based ACLs scope permissions by role, so staff see only the records their job requires.",
  },
  {
    icon: Server,
    label: "Infrastructure",
    desc: "Hosted on AWS in the ca-central-1 region (Canada).",
  },
  {
    icon: Database,
    label: "Tenant Isolation",
    desc: "Customer data is siloed per tenant rather than pooled across accounts.",
  },
  {
    icon: FileCheck,
    label: "Audit Records",
    desc: "Financial and HR actions produce audit-ready records that can be exported for review.",
  },
];

export default function Security() {
  return (
    <DcPage>
      <section className="overflow-hidden relative pt-24 pb-20 md:pt-32" style={{ background: "var(--landing-bg)" }}>
        <AmbientField />
        <Spotlight />
        <div className="container px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-3xl">
            <p className="font-mono text-[10px] uppercase tracking-[0.2em]" style={{ color: "var(--landing-text-secondary)" }}>
              Security &amp; Compliance
            </p>
            <h1 className="mt-3 text-4xl font-extrabold tracking-tight sm:text-5xl" style={{ color: "var(--landing-text)" }}>
              Your data, and who can reach it
            </h1>
            <p className="mt-6 text-lg leading-relaxed" style={{ color: "var(--landing-text-muted)" }}>
              AI2Aim handles HR and financial records, so it is reasonable to ask
              exactly how they are stored, who can see them, and what we have
              been independently audited against. Here is what we can state
              plainly.
            </p>
          </div>

          {/* ── Controls in place ── */}
          <div className="mx-auto mt-16 max-w-5xl">
            <h2 className="text-xl font-bold" style={{ color: "var(--landing-text)" }}>
              Controls in place
            </h2>
            <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {CONTROLS.map((c) => {
                const Icon = c.icon;
                return (
                  <div
                    key={c.label}
                    className="rounded-2xl p-6"
                    style={{
                      background: "var(--landing-card-bg)",
                      border: "1px solid var(--landing-card-border)",
                      boxShadow: "var(--landing-card-shadow)",
                    }}
                  >
                    <Icon className="h-5 w-5 text-[var(--acc2)]" aria-hidden="true" />
                    <h3 className="mt-3 text-sm font-bold" style={{ color: "var(--landing-text)" }}>
                      {c.label}
                    </h3>
                    <p className="mt-1.5 text-sm leading-relaxed" style={{ color: "var(--landing-text-muted)" }}>
                      {c.desc}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>

          {/* ── Certifications ── */}
          <div className="mx-auto mt-16 max-w-3xl">
            <h2 className="text-xl font-bold" style={{ color: "var(--landing-text)" }}>
              Certifications
            </h2>
            <div
              className="mt-6 rounded-2xl border-2 border-dashed p-6 sm:p-8"
              style={{ borderColor: "var(--landing-card-border)" }}
            >
              <p className="text-sm leading-relaxed" style={{ color: "var(--landing-text-muted)" }}>
                We publish certifications here only once the audit is complete
                and the report exists. If your procurement process needs our
                current compliance position, our security questionnaire
                responses, or a signed DPA, contact us and we will send the
                current documentation directly.
              </p>
              <Link
                to="/contact"
                className="mt-5 inline-flex h-10 items-center rounded-lg px-4 text-sm font-semibold"
                style={{ background: "var(--landing-surface)", border: "1px solid var(--landing-glass-border)", color: "var(--landing-text)" }}
              >
                Request compliance documentation
              </Link>
            </div>
          </div>

          <div className="mx-auto mt-16 max-w-3xl text-center">
            <p className="text-sm" style={{ color: "var(--landing-text-muted)" }}>
              Have a security question this page does not answer?
            </p>
            <Link
              to="/contact"
              className="btn-electric mt-4 inline-flex h-11 items-center rounded-xl px-6 text-sm font-semibold"
            >
              Talk to us
            </Link>
          </div>
        </div>
      </section>
    </DcPage>
  );
}
